import React, { useEffect, useState } from "react";
import { PageHeader, Card } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatErr, resolveImage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Key, Building, ShieldCheck, UserPlus, Edit2, Trash2, Shield, Users, Eye, EyeOff,
  Globe, Megaphone, Palette, Share2, Save, Sparkles, Check, Upload, Image as ImageIcon,
  Database, Download, HardDrive, FileJson, Clock
} from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("whitelabel"); // "whitelabel" | "admins" | "profile"

  // Password state
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passLoading, setPassLoading] = useState(false);

  // MFA state
  const [mfaStatus, setMfaStatus] = useState(user?.mfa_enabled || false);
  const [mfaQrCode, setMfaQrCode] = useState("");
  const [mfaSecretKey, setMfaSecretKey] = useState("");
  const [mfaVerifyCode, setMfaVerifyCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);

  // Target Admin MFA setup modal state
  const [adminMfaSetupModal, setAdminMfaSetupModal] = useState(false);
  const [adminMfaData, setAdminMfaData] = useState(null);
  const [loadingAdminMfa, setLoadingAdminMfa] = useState(false);

  // Backup configuration state
  const [backupConfig, setBackupConfig] = useState({
    folder_path: "",
    schedule_type: "daily",
    backup_time: "02:00",
    enabled: true,
    last_backup_at: null,
    last_backup_status: "Never executed",
  });
  const [loadingBackupConfig, setLoadingBackupConfig] = useState(false);
  const [savingBackupConfig, setSavingBackupConfig] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);

  useEffect(() => {
    if (user?.mfa_enabled !== undefined) {
      setMfaStatus(user.mfa_enabled);
    }
    loadBackupConfig();
  }, [user]);

  async function loadBackupConfig() {
    try {
      setLoadingBackupConfig(true);
      const { data } = await api.get("/backup/config");
      if (data) {
        setBackupConfig(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBackupConfig(false);
    }
  }

  async function handleSaveBackupConfig(e) {
    e?.preventDefault();
    try {
      setSavingBackupConfig(true);
      const { data } = await api.post("/backup/config", backupConfig);
      setBackupConfig(data);
      toast.success("Backup schedule & folder path configuration saved!");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSavingBackupConfig(false);
    }
  }

  const [importingBackup, setImportingBackup] = useState(false);

  async function handleImportBackup(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setImportingBackup(true);
    try {
      const res = await api.post("/backup/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message || "Database data imported successfully!");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail || err) || "Failed to import database file");
    } finally {
      setImportingBackup(false);
      e.target.value = "";
    }
  }

  async function handleRunLocalBackup() {
    if (!backupConfig.folder_path.strip?.() && !backupConfig.folder_path) {
      toast.error("Please specify a valid folder path first");
      return;
    }
    try {
      setRunningBackup(true);
      const { data } = await api.post("/backup/run-local");
      toast.success(data.message);
      loadBackupConfig();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setRunningBackup(false);
    }
  }

  async function handleFetchAdminMfaSetup(adminId) {
    setLoadingAdminMfa(true);
    try {
      const { data } = await api.post(`/admins/${adminId}/mfa-setup`);
      setAdminMfaData(data);
      setAdminMfaSetupModal(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoadingAdminMfa(false);
    }
  }

  async function handleSetupMfa() {
    setMfaLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setMfaQrCode(data.qr_code);
      setMfaSecretKey(data.secret);
      toast.info("Scan the QR code in Google Authenticator app.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleEnableMfa() {
    if (!mfaVerifyCode || mfaVerifyCode.length !== 6) {
      toast.error("Please enter a 6-digit Google Authenticator code");
      return;
    }
    setMfaLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: mfaVerifyCode });
      toast.success(data.message);
      setMfaStatus(true);
      setMfaQrCode("");
      setMfaVerifyCode("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleDisableMfa() {
    setMfaLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/disable", { code: mfaVerifyCode || undefined });
      toast.success(data.message);
      setMfaStatus(false);
      setMfaVerifyCode("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setMfaLoading(false);
    }
  }

  // Admin Management state
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [confirmAdminPassword, setConfirmAdminPassword] = useState("");
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetAdmin, setDeleteTargetAdmin] = useState(null);
  const DEFAULT_PERMISSIONS = {
    products: true,
    customers: true,
    pricing: true,
    orders: true,
    inventory: true,
    deliveries: true,
    routes: true,
    drivers: true,
    payments: true,
    ledger: true,
    analytics: true,
    reports: true,
    audit: false,
    settings: false,
  };

  const [adminFormData, setAdminFormData] = useState({
    name: "", username: "", email: "", mobile: "", password: "", status: true,
    permissions: { ...DEFAULT_PERMISSIONS },
  });

  // Digital Marketing & White Labeling state
  const [loadingWhitelabel, setLoadingWhitelabel] = useState(true);
  const [savingWhitelabel, setSavingWhitelabel] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [wlData, setWlData] = useState({
    company_name: "Omkar Milk Distributor",
    tagline: "Distributor",
    company_description: "Leading distributor of fresh milk and premium dairy products.",
    logo_url: "",
    primary_color: "#0047AB",
    accent_color: "#10B981",
    contact_email: "",
    contact_phone: "",
    website_url: "",
    facebook_url: "",
    instagram_url: "",
    whatsapp_number: "",
    custom_domain: "",
    meta_title: "Dairy Distributor Management System",
    meta_description: "",
    footer_text: "Omkar Milk Distributor",
  });

  useEffect(() => {
    loadAdmins();
    loadWhitelabel();
  }, []);

  async function loadAdmins() {
    setLoadingAdmins(true);
    try {
      const res = await api.get("/admins");
      setAdmins(res.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to load admins");
    } finally {
      setLoadingAdmins(false);
    }
  }

  async function loadWhitelabel() {
    setLoadingWhitelabel(true);
    try {
      const res = await api.get("/whitelabel");
      if (res.data) setWlData(res.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to load branding settings");
    } finally {
      setLoadingWhitelabel(false);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate client-side file size (Max 2MB = 2 * 1024 * 1024 bytes)
    const MAX_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error(`File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds the maximum allowed limit of 2 MB.`);
      e.target.value = "";
      return;
    }

    // Validate client-side file extension
    const allowedExts = [".png", ".jpg", ".jpeg", ".svg", ".webp"];
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExts.includes(fileExt)) {
      toast.error(`Invalid file format "${fileExt}". Please upload a PNG, JPG, JPEG, SVG, or WEBP image.`);
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploadingLogo(true);
    try {
      const res = await api.post("/whitelabel/upload-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setWlData(res.data);
      toast.success("Logo uploaded successfully!");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  }

  async function handleSaveWhitelabel(e) {
    e.preventDefault();
    setSavingWhitelabel(true);
    try {
      const res = await api.post("/whitelabel", wlData);
      setWlData(res.data);
      toast.success("Digital Marketing & White Labeling settings saved!");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to save white labeling settings");
    } finally {
      setSavingWhitelabel(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    setPassLoading(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast.success("Password updated successfully");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setPassLoading(false);
    }
  }

  function handleOpenCreate() {
    setEditAdmin(null);
    setConfirmAdminPassword("");
    setShowAdminPassword(false);
    setAdminFormData({
      name: "", username: "", email: "", mobile: "", password: "", status: true, mfa_enabled: false,
      permissions: { ...DEFAULT_PERMISSIONS },
    });
    setOpenModal(true);
  }

  function handleOpenEdit(adminItem) {
    setEditAdmin(adminItem);
    const existingPassword = adminItem.plain_password || "";
    setConfirmAdminPassword(existingPassword);
    setShowAdminPassword(false);
    setAdminFormData({
      name: adminItem.name || "",
      username: adminItem.username || "",
      email: adminItem.email || "",
      mobile: adminItem.mobile || "",
      password: existingPassword,
      status: adminItem.status !== false,
      mfa_enabled: adminItem.mfa_enabled || false,
      permissions: adminItem.permissions || { ...DEFAULT_PERMISSIONS },
    });
    setOpenModal(true);
  }

  async function handleSaveAdmin(e) {
    e.preventDefault();
    if (!adminFormData.password) {
      toast.error("Password is required");
      return;
    }
    if (adminFormData.password !== confirmAdminPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSavingAdmin(true);
    try {
      const payload = {
        name: adminFormData.name,
        username: adminFormData.username,
        email: adminFormData.email,
        mobile: adminFormData.mobile,
        status: adminFormData.status,
        mfa_enabled: adminFormData.mfa_enabled,
        permissions: adminFormData.permissions,
        password: adminFormData.password,
      };
      if (editAdmin) {
        await api.put(`/admins/${editAdmin.id}`, payload);
        toast.success("Admin details updated successfully!");
      } else {
        await api.post("/admins", payload);
        toast.success("New admin created successfully!");
      }
      setOpenModal(false);
      loadAdmins();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSavingAdmin(false);
    }
  }

  function promptDeleteAdmin(adminItem) {
    setDeleteTargetAdmin(adminItem);
    setDeleteConfirmModal(true);
  }

  async function confirmDeleteAdminAction() {
    if (!deleteTargetAdmin) return;
    try {
      await api.delete(`/admins/${deleteTargetAdmin.id}`);
      toast.success("Admin deleted successfully");
      loadAdmins();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to delete admin");
    } finally {
      setDeleteConfirmModal(false);
      setDeleteTargetAdmin(null);
    }
  }

  return (
    <div data-testid="settings-page" className="max-w-5xl space-y-6">
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        description="Configure Digital Marketing white labeling, brand identity, admin accounts, and security."
      />

      {/* Tabs Bar */}
      <div className="flex border-b border-slate-200 gap-2">
        <button
          onClick={() => setActiveTab("whitelabel")}
          className={`pb-3 px-4 font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === "whitelabel"
              ? "border-cobalt-600 text-cobalt-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Sparkles size={16} /> Digital Marketing & White Labeling
        </button>
        <button
          onClick={() => setActiveTab("admins")}
          className={`pb-3 px-4 font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === "admins"
              ? "border-cobalt-600 text-cobalt-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Users size={16} /> Admin Accounts
        </button>
        <button
          onClick={() => setActiveTab("profile")}
          className={`pb-3 px-4 font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === "profile"
              ? "border-cobalt-600 text-cobalt-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Key size={16} /> Security & Profile
        </button>
        <button
          onClick={() => setActiveTab("backup")}
          className={`pb-3 px-4 font-semibold text-sm flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === "backup"
              ? "border-cobalt-600 text-cobalt-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Database size={16} /> Database Backup
        </button>
      </div>

      {/* TAB 1: Digital Marketing & White Labeling */}
      {activeTab === "whitelabel" && (
        <form onSubmit={handleSaveWhitelabel} className="space-y-6" data-testid="whitelabel-form">
          {/* Brand Identity & Theme */}
          <Card testid="card-brand-identity">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Palette size={18} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">Brand Identity & White Labeling</h3>
                <p className="text-xs text-slate-500">Rebrand the portal with your company name, logo, and custom color scheme.</p>
              </div>
            </div>
            {loadingWhitelabel ? (
              <div className="py-6 text-center text-sm text-slate-500">Loading white label settings…</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">Company / Business Name</Label>
                  <Input
                    id="company_name"
                    value={wlData.company_name}
                    onChange={(e) => setWlData({ ...wlData, company_name: e.target.value })}
                    placeholder="e.g. Omkar Milk Distributor"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="tagline">Tagline / Subheading</Label>
                  <Input
                    id="tagline"
                    value={wlData.tagline}
                    onChange={(e) => setWlData({ ...wlData, tagline: e.target.value })}
                    placeholder="e.g. Premium Dairy Products"
                    className="mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="company_description">Project / Business Description (Displayed on Search & Overview)</Label>
                  <textarea
                    id="company_description"
                    rows={3}
                    value={wlData.company_description || ""}
                    onChange={(e) => setWlData({ ...wlData, company_description: e.target.value })}
                    placeholder="Provide a brief description of your business, services, and distribution coverage that will appear alongside your logo and business name when users search for your project."
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cobalt-500 focus:border-transparent"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">This description is shown with your logo and company name when searching or sharing your portal.</p>
                </div>
                <div className="sm:col-span-2">
                  <Label>Company Brand Logo</Label>
                  <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {wlData.logo_url ? (
                        <div className="w-20 h-16 rounded border border-slate-200 bg-white p-1 flex items-center justify-center overflow-hidden shrink-0">
                          <img src={resolveImage(wlData.logo_url)} alt="Uploaded logo" className="max-h-full max-w-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-20 h-16 rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                          <ImageIcon size={24} />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {wlData.logo_url ? "Current Logo Active" : "Upload Brand Logo"}
                        </p>
                        {/* Required Logo Specifications & Aspect Ratio */}
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                          <span className="bg-slate-200/70 text-slate-700 px-2 py-0.5 rounded font-mono text-[11px]">Ratio: 3:1 (Horizontal)</span>
                          <span className="bg-slate-200/70 text-slate-700 px-2 py-0.5 rounded font-mono text-[11px]">Rec. Size: 512 × 170 px</span>
                          <span className="bg-slate-200/70 text-slate-700 px-2 py-0.5 rounded font-mono text-[11px]">Formats: PNG, JPG, JPEG, SVG, WEBP</span>
                          <span className="bg-slate-200/70 text-slate-700 px-2 py-0.5 rounded font-mono text-[11px]">Max File: 2MB</span>
                        </div>
                      </div>
                    </div>
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-sm shrink-0">
                      <Upload size={16} className="text-cobalt-600" />
                      {uploadingLogo ? "Uploading…" : wlData.logo_url ? "Change Logo" : "Upload Logo"}
                      <input
                        type="file"
                        accept="image/png,image/jpg,image/jpeg,image/svg+xml,image/webp,.png,.jpg,.jpeg,.svg,.webp"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <Label htmlFor="primary_color">Brand Primary Color</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <input
                      type="color"
                      id="primary_color"
                      value={wlData.primary_color || "#0047AB"}
                      onChange={(e) => setWlData({ ...wlData, primary_color: e.target.value })}
                      className="w-10 h-10 rounded border border-slate-200 cursor-pointer p-0.5"
                    />
                    <Input
                      value={wlData.primary_color}
                      onChange={(e) => setWlData({ ...wlData, primary_color: e.target.value })}
                      placeholder="#0047AB"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="accent_color">Brand Accent Color</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <input
                      type="color"
                      id="accent_color"
                      value={wlData.accent_color || "#10B981"}
                      onChange={(e) => setWlData({ ...wlData, accent_color: e.target.value })}
                      className="w-10 h-10 rounded border border-slate-200 cursor-pointer p-0.5"
                    />
                    <Input
                      value={wlData.accent_color}
                      onChange={(e) => setWlData({ ...wlData, accent_color: e.target.value })}
                      placeholder="#10B981"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Official Contact, Tax & Banking Details (Invoice PDF) */}
          <Card testid="card-tax-banking">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Building size={18} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">Official Tax, Regulatory & Banking Details</h3>
                <p className="text-xs text-slate-500">Configure phone, email, GSTIN, FSSAI, address, and bank information printed on PDF invoices.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_phone">Official Contact Mobile</Label>
                <Input
                  id="contact_phone"
                  value={wlData.contact_phone || ""}
                  onChange={(e) => setWlData({ ...wlData, contact_phone: e.target.value })}
                  placeholder="+91 8010589178"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contact_email">Official Email Address</Label>
                <Input
                  id="contact_email"
                  value={wlData.contact_email || ""}
                  onChange={(e) => setWlData({ ...wlData, contact_email: e.target.value })}
                  placeholder="orders@omkardairy.com"
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">Registered Office Address</Label>
                <Input
                  id="address"
                  value={wlData.address || ""}
                  onChange={(e) => setWlData({ ...wlData, address: e.target.value })}
                  placeholder="Shop No. 4, Wholesale Market, Porvorim, Goa - 403521"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="gst_number">Supplier GSTIN Number</Label>
                <Input
                  id="gst_number"
                  value={wlData.gst_number || ""}
                  onChange={(e) => setWlData({ ...wlData, gst_number: e.target.value })}
                  placeholder="30AAAAA0000A1Z5"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="fssai_number">FSSAI License Number</Label>
                <Input
                  id="fssai_number"
                  value={wlData.fssai_number || ""}
                  onChange={(e) => setWlData({ ...wlData, fssai_number: e.target.value })}
                  placeholder="11521000000123"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="bank_name">Bank Name</Label>
                <Input
                  id="bank_name"
                  value={wlData.bank_name || ""}
                  onChange={(e) => setWlData({ ...wlData, bank_name: e.target.value })}
                  placeholder="HDFC Bank"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="bank_account_no">Account Number</Label>
                <Input
                  id="bank_account_no"
                  value={wlData.bank_account_no || ""}
                  onChange={(e) => setWlData({ ...wlData, bank_account_no: e.target.value })}
                  placeholder="50200012345678"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="bank_ifsc">IFSC Code</Label>
                <Input
                  id="bank_ifsc"
                  value={wlData.bank_ifsc || ""}
                  onChange={(e) => setWlData({ ...wlData, bank_ifsc: e.target.value })}
                  placeholder="HDFC0001234"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="upi_id">UPI Payment ID</Label>
                <Input
                  id="upi_id"
                  value={wlData.upi_id || ""}
                  onChange={(e) => setWlData({ ...wlData, upi_id: e.target.value })}
                  placeholder="omkarmilk@upi"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>
          </Card>

          {/* Social Media & Contact Links (Digital Marketing) */}
          <Card testid="card-social-marketing">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Share2 size={18} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">Digital Marketing & Social Channels</h3>
                <p className="text-xs text-slate-500">Connect your website, WhatsApp, and social media handles for marketing.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="whatsapp_number">WhatsApp Business Number</Label>
                <Input
                  id="whatsapp_number"
                  value={wlData.whatsapp_number}
                  onChange={(e) => setWlData({ ...wlData, whatsapp_number: e.target.value })}
                  placeholder="+91 9876543210"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="website_url">Official Website URL</Label>
                <Input
                  id="website_url"
                  value={wlData.website_url}
                  onChange={(e) => setWlData({ ...wlData, website_url: e.target.value })}
                  placeholder="https://omkarmilk.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="instagram_url">Instagram Profile Link</Label>
                <Input
                  id="instagram_url"
                  value={wlData.instagram_url}
                  onChange={(e) => setWlData({ ...wlData, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/omkarmilk"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="facebook_url">Facebook Page Link</Label>
                <Input
                  id="facebook_url"
                  value={wlData.facebook_url}
                  onChange={(e) => setWlData({ ...wlData, facebook_url: e.target.value })}
                  placeholder="https://facebook.com/omkarmilk"
                  className="mt-1"
                />
              </div>
            </div>
          </Card>

          {/* SEO & Domain Configuration */}
          <Card testid="card-seo-domain">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Globe size={18} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">SEO & White Label Domain</h3>
                <p className="text-xs text-slate-500">Configure white-label domain name and search engine meta titles.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="custom_domain">White Label Custom Domain</Label>
                <Input
                  id="custom_domain"
                  value={wlData.custom_domain}
                  onChange={(e) => setWlData({ ...wlData, custom_domain: e.target.value })}
                  placeholder="portal.yourcompany.com"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="meta_title">Portal Meta Title (SEO)</Label>
                <Input
                  id="meta_title"
                  value={wlData.meta_title}
                  onChange={(e) => setWlData({ ...wlData, meta_title: e.target.value })}
                  placeholder="Dairy Distributor Management System"
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="footer_text">Footer Branding Text</Label>
                <Input
                  id="footer_text"
                  value={wlData.footer_text}
                  onChange={(e) => setWlData({ ...wlData, footer_text: e.target.value })}
                  placeholder="© 2026 Omkar Milk Distributor"
                  className="mt-1"
                />
              </div>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={savingWhitelabel} className="bg-cobalt-600 hover:bg-cobalt-700 text-white font-medium flex items-center gap-2 px-6">
              <Save size={16} /> {savingWhitelabel ? "Saving Settings…" : "Save White Label Settings"}
            </Button>
          </div>
        </form>
      )}

      {/* TAB 2: Admin Accounts */}
      {activeTab === "admins" && (
        <Card testid="card-admin-management">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Users size={20} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">Admin Accounts</h3>
                <p className="text-xs text-slate-500">Add, edit, or disable administrator accounts for the portal.</p>
              </div>
            </div>
            <Button
              onClick={handleOpenCreate}
              data-testid="btn-add-admin"
              className="bg-cobalt-600 hover:bg-cobalt-700 text-white font-medium flex items-center gap-2 self-start sm:self-auto"
            >
              <UserPlus size={16} /> Add New Admin
            </Button>
          </div>

          {loadingAdmins ? (
            <div className="py-8 text-center text-sm text-slate-500">Loading admin accounts…</div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Username</TableHead>
                    <TableHead className="font-semibold text-slate-700">Email</TableHead>
                    <TableHead className="font-semibold text-slate-700">Mobile</TableHead>
                    <TableHead className="font-semibold text-slate-700">Status</TableHead>
                    <TableHead className="font-semibold text-slate-700">MFA</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-6 text-slate-500 text-sm">
                        No admin accounts found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    admins.map((adm) => (
                      <TableRow key={adm.id} data-testid={`admin-row-${adm.id}`}>
                        <TableCell className="font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            <Shield size={16} className={adm.is_super_admin ? "text-amber-500" : "text-cobalt-500"} />
                            <span>{adm.name}</span>
                            {adm.is_super_admin ? (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Super Admin</Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-600 text-[10px]">Admin</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-700">{adm.username}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{adm.email || "—"}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{adm.mobile || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={adm.status !== false ? "success" : "secondary"}>
                            {adm.status !== false ? "Active" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={adm.mfa_enabled ? "success" : "outline"} className={adm.mfa_enabled ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "text-slate-500"}>
                            {adm.mfa_enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenEdit(adm)}
                            data-testid={`btn-edit-admin-${adm.id}`}
                            className="h-8 px-2.5"
                          >
                            <Edit2 size={14} className="text-slate-600" />
                          </Button>
                          {!adm.is_super_admin && user?.id !== adm.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => promptDeleteAdmin(adm)}
                              data-testid={`btn-delete-admin-${adm.id}`}
                              className="h-8 px-2.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {/* TAB 3: Security & Profile */}
      {activeTab === "profile" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Password Change Card */}
          <Card testid="card-security-settings">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Key size={18} className="text-cobalt-600" />
              <h3 className="font-heading font-semibold text-slate-900">Change Your Password</h3>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="old_password">Current Password</Label>
                <Input
                  id="old_password"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <Button type="submit" disabled={passLoading} className="bg-cobalt-600 hover:bg-cobalt-700 text-white w-full">
                {passLoading ? "Updating…" : "Update Password"}
              </Button>
            </form>
          </Card>

          {/* Google Authenticator MFA Card */}
          <Card testid="card-mfa-settings">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className={mfaStatus ? "text-emerald-600" : "text-amber-600"} />
                <h3 className="font-heading font-semibold text-slate-900">Google Authenticator MFA</h3>
              </div>
              <Badge variant={mfaStatus ? "success" : "secondary"}>
                {mfaStatus ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Enhance admin account security by requiring a 6-digit Google Authenticator code upon signing in.
            </p>

            {!mfaStatus ? (
              <div className="space-y-4">
                {!mfaQrCode ? (
                  <Button
                    onClick={handleSetupMfa}
                    disabled={mfaLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center justify-center gap-2"
                  >
                    <Sparkles size={16} /> {mfaLoading ? "Generating Setup QR…" : "Setup Google Authenticator"}
                  </Button>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4">
                    <div className="text-center">
                      <div className="text-xs font-semibold text-slate-700 mb-2">1. Scan QR Code with Google Authenticator App</div>
                      <img src={mfaQrCode} alt="Google Authenticator QR Code" className="w-40 h-40 mx-auto border rounded-md p-1 bg-white" />
                      <div className="text-[11px] font-mono text-slate-500 mt-2 bg-white px-2 py-1 rounded border inline-block select-all">
                        Secret: {mfaSecretKey}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold text-slate-700 mb-1">2. Enter 6-digit Code from Authenticator to Enable</div>
                      <Input
                        type="text"
                        maxLength={6}
                        value={mfaVerifyCode}
                        onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="123456"
                        className="font-mono text-center text-lg tracking-widest bg-white"
                      />
                    </div>

                    <Button
                      onClick={handleEnableMfa}
                      disabled={mfaLoading || mfaVerifyCode.length !== 6}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                    >
                      {mfaLoading ? "Enabling…" : "Verify & Enable MFA"}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-900">
                  <Check size={16} className="text-emerald-600" /> Google Authenticator MFA is active for your account.
                </div>
                <Button
                  onClick={handleDisableMfa}
                  disabled={mfaLoading}
                  variant="outline"
                  className="w-full border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 font-medium"
                >
                  {mfaLoading ? "Disabling…" : "Disable Google Authenticator MFA"}
                </Button>
              </div>
            )}
          </Card>

          <Card testid="card-distributor-info">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Building size={18} className="text-cobalt-600" />
              <h3 className="font-heading font-semibold text-slate-900">Distributor Profile</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold block">Business Name</span>
                <span className="font-medium text-slate-800">{wlData.company_name || "Omkar Milk Distributor"}</span>
              </div>
              <div>
                <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold block">System Version</span>
                <span className="font-medium text-slate-800">v1.1 (MySQL DDMS)</span>
              </div>
              <div>
                <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold block">Security Level</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 mt-1">
                  <ShieldCheck size={14} /> Encrypted & Active
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 4: Database Backup */}
      {activeTab === "backup" && (
        <div className="space-y-6">
          <Card testid="card-database-backup">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
              <Database size={20} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">System Data Backup & Scheduled Exports</h3>
                <p className="text-xs text-slate-500">Configure auto-backup folder paths, frequency schedules, or trigger instant backups.</p>
              </div>
            </div>

            <form onSubmit={handleSaveBackupConfig} className="space-y-6">
              <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                  <HardDrive size={18} className="text-cobalt-600" />
                  <span>Automated Backup Location & Schedule Configuration</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="backup_folder_path">Local Storage Folder Path</Label>
                    <Input
                      id="backup_folder_path"
                      type="text"
                      value={backupConfig.folder_path}
                      onChange={(e) => setBackupConfig({ ...backupConfig, folder_path: e.target.value })}
                      placeholder="e.g. D:\Backups\OmkarDairy or C:\Users\Public\DDMS_Backups"
                      className="font-mono text-sm mt-1 bg-white"
                      required
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Enter the absolute folder path on the server where automated database backup files will be saved.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="schedule_type">Backup Frequency (Type)</Label>
                    <select
                      id="schedule_type"
                      value={backupConfig.schedule_type}
                      onChange={(e) => setBackupConfig({ ...backupConfig, schedule_type: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cobalt-500"
                    >
                      <option value="daily">Daily Backup</option>
                      <option value="weekly">Weekly Backup</option>
                      <option value="monthly">Monthly Backup</option>
                      <option value="disabled">Disabled (Manual Only)</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="backup_time">Scheduled Backup Time (24h)</Label>
                    <Input
                      id="backup_time"
                      type="time"
                      value={backupConfig.backup_time}
                      onChange={(e) => setBackupConfig({ ...backupConfig, backup_time: e.target.value })}
                      className="font-mono text-sm mt-1 bg-white"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-slate-200/80">
                  <div className="text-xs text-slate-500">
                    Last Backup Status: <b className="text-slate-700">{backupConfig.last_backup_status || "Never executed"}</b>
                    {backupConfig.last_backup_at && ` (${backupConfig.last_backup_at})`}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRunLocalBackup}
                      disabled={runningBackup || !backupConfig.folder_path}
                      className="border-cobalt-300 text-cobalt-700 hover:bg-cobalt-50 text-xs font-medium"
                    >
                      <HardDrive size={14} className="mr-1.5" />
                      {runningBackup ? "Saving to Folder..." : "Save Backup to Specified Folder"}
                    </Button>

                    <Button
                      type="submit"
                      disabled={savingBackupConfig}
                      className="bg-cobalt-600 hover:bg-cobalt-700 text-white text-xs font-medium"
                    >
                      <Save size={14} className="mr-1.5" />
                      {savingBackupConfig ? "Saving..." : "Save Configuration"}
                    </Button>
                  </div>
                </div>
              </div>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-100">
              <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <FileJson size={18} className="text-cobalt-600" />
                    <span>Download Full Backup File (.json)</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Download an instant database backup directly to your browser's download directory.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await api.get("/backup/export", { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      const dateStr = new Date().toISOString().slice(0, 10);
                      link.setAttribute("download", `ddms_backup_${dateStr}.json`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success("Database backup downloaded successfully!");
                    } catch (err) {
                      toast.error("Failed to export database backup: " + formatErr(err));
                    }
                  }}
                  className="bg-slate-800 hover:bg-slate-900 text-white font-medium flex items-center justify-center gap-2 w-full text-xs"
                >
                  <Download size={15} /> Instant Browser Download
                </Button>
              </div>

              {/* Upload Database Data Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <Upload size={18} className="text-emerald-600" />
                    <span>Upload & Restore Backup Data</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Select and upload a JSON database backup or SQL dump file to populate or restore project data.
                  </p>
                </div>
                <label className="cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center justify-center gap-2 w-full py-2 px-3 rounded-md text-xs shadow-sm transition-colors text-center">
                  <Upload size={15} />
                  {importingBackup ? "Uploading Data…" : "Upload Backup File (.json / .sql)"}
                  <input
                    type="file"
                    accept=".json,.sql"
                    onChange={handleImportBackup}
                    disabled={importingBackup}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="bg-emerald-50/50 border border-emerald-200/80 rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-emerald-900 font-semibold">
                  <Clock size={18} className="text-emerald-600" />
                  <span>Backup Details & Safety</span>
                </div>
                <ul className="text-xs text-emerald-800 space-y-2 list-disc list-inside">
                  <li>Folder Backups automatically save formatted `.json` files to your custom path.</li>
                  <li>Upload option supports restoring <b>.json</b> and <b>.sql</b> data dump files.</li>
                  <li>Complete snapshot includes <b>Customers, Products, Ledgers & Admin Settings</b>.</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Admin Add / Edit Dialog Modal */}
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editAdmin ? "Edit Admin Account" : "Add New Admin"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveAdmin} className="space-y-4 py-2">
            <div>
              <Label htmlFor="admin_name">Full Name</Label>
              <Input
                id="admin_name"
                data-testid="input-admin-name"
                value={adminFormData.name}
                onChange={(e) => setAdminFormData({ ...adminFormData, name: e.target.value })}
                placeholder="e.g. Ramesh Shah"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="admin_username">Username</Label>
              <Input
                id="admin_username"
                data-testid="input-admin-username"
                value={adminFormData.username}
                onChange={(e) => setAdminFormData({ ...adminFormData, username: e.target.value })}
                placeholder="e.g. ramesh_admin"
                required
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="admin_email">Email (Optional)</Label>
                <Input
                  id="admin_email"
                  type="email"
                  data-testid="input-admin-email"
                  value={adminFormData.email}
                  onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                  placeholder="ramesh@omkarmilk.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="admin_mobile">Mobile (Optional)</Label>
                <Input
                  id="admin_mobile"
                  data-testid="input-admin-mobile"
                  value={adminFormData.mobile}
                  onChange={(e) => setAdminFormData({ ...adminFormData, mobile: e.target.value })}
                  placeholder="9876543210"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="admin_password">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="admin_password"
                  type={showAdminPassword ? "text" : "password"}
                  data-testid="input-admin-password"
                  value={adminFormData.password}
                  onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
                  placeholder="Password"
                  required={!editAdmin}
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAdminPassword(!showAdminPassword);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none p-1"
                  aria-label="Toggle password visibility"
                  title={showAdminPassword ? "Hide Password" : "Show Password"}
                >
                  {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="admin_confirm_password">
                Confirm Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="admin_confirm_password"
                  type={showAdminPassword ? "text" : "password"}
                  data-testid="input-admin-confirm-password"
                  value={confirmAdminPassword}
                  onChange={(e) => setConfirmAdminPassword(e.target.value)}
                  placeholder="Confirm Password"
                  required={!editAdmin || !!adminFormData.password}
                  className="pr-10 font-mono"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAdminPassword(!showAdminPassword);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none p-1"
                  aria-label="Toggle password visibility"
                  title={showAdminPassword ? "Hide Password" : "Show Password"}
                >
                  {showAdminPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {/* Granular Module Permissions (Only customizable for normal Admins) */}
            {(!editAdmin || !editAdmin.is_super_admin) && (
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50/70 space-y-2">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider block">
                  Admin Access & Feature Permissions
                </Label>
                <p className="text-[11px] text-slate-500">Configure which modules this admin is allowed to manage:</p>
                <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                  {[
                    { key: "products", label: "Products Catalog" },
                    { key: "customers", label: "Customers & Accounts" },
                    { key: "pricing", label: "Pricing Matrix" },
                    { key: "orders", label: "Orders & Billing" },
                    { key: "inventory", label: "Stock & Inventory" },
                    { key: "deliveries", label: "Deliveries Management" },
                    { key: "routes", label: "Delivery Routes" },
                    { key: "drivers", label: "Drivers" },
                    { key: "payments", label: "Payments & Collections" },
                    { key: "ledger", label: "Customer Ledger" },
                    { key: "analytics", label: "Analytics & Intelligence" },
                    { key: "reports", label: "Reports & Export Hub" },
                    { key: "audit", label: "Audit Logs Access" },
                    { key: "settings", label: "White Labeling Settings" },
                  ].map((item) => (
                    <label key={item.key} className="flex items-center gap-1.5 cursor-pointer text-slate-700 hover:text-slate-900">
                      <input
                        type="checkbox"
                        checked={!!adminFormData.permissions?.[item.key]}
                        onChange={(e) => setAdminFormData({
                          ...adminFormData,
                          permissions: {
                            ...adminFormData.permissions,
                            [item.key]: e.target.checked
                          }
                        })}
                        className="rounded border-slate-300 text-cobalt-600 focus:ring-cobalt-500"
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="admin_status"
                  data-testid="checkbox-admin-status"
                  checked={adminFormData.status}
                  onChange={(e) => setAdminFormData({ ...adminFormData, status: e.target.checked })}
                  className="w-4 h-4 text-cobalt-600 rounded"
                />
                <Label htmlFor="admin_status" className="text-sm font-normal cursor-pointer">
                  Active Admin Account
                </Label>
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="admin_mfa_enabled"
                    data-testid="checkbox-admin-mfa"
                    checked={adminFormData.mfa_enabled}
                    onChange={(e) => setAdminFormData({ ...adminFormData, mfa_enabled: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <Label htmlFor="admin_mfa_enabled" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                    <ShieldCheck size={16} className="text-emerald-600" />
                    Enable Google Authenticator MFA for this Admin
                  </Label>
                </div>

                {editAdmin && adminFormData.mfa_enabled && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleFetchAdminMfaSetup(editAdmin.id)}
                    disabled={loadingAdminMfa}
                    className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Sparkles size={13} className="mr-1" />
                    {loadingAdminMfa ? "Generating..." : "View MFA QR Code"}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpenModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingAdmin} className="bg-cobalt-600 hover:bg-cobalt-700 text-white">
                {savingAdmin ? "Saving…" : editAdmin ? "Update Admin" : "Create Admin"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin MFA QR Code Popup Dialog */}
      <Dialog open={adminMfaSetupModal} onOpenChange={setAdminMfaSetupModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 font-heading">
              <ShieldCheck size={20} className="text-emerald-600" />
              Google Authenticator MFA Setup
            </DialogTitle>
          </DialogHeader>

          {adminMfaData && (
            <div className="space-y-4 text-center py-2">
              <p className="text-xs text-slate-600 leading-relaxed">
                Scan this QR code with the Google Authenticator app on <b>{editAdmin?.name}'s</b> phone to register their account:
              </p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <img
                  src={adminMfaData.qr_code}
                  alt="Admin MFA QR Code"
                  className="w-44 h-44 mx-auto border rounded-md p-1 bg-white"
                />
                <div className="text-xs font-mono text-slate-600 mt-3 bg-white px-3 py-1.5 rounded border inline-block select-all">
                  Secret: <b>{adminMfaData.secret}</b>
                </div>
              </div>

              <div className="bg-emerald-50/70 border border-emerald-200 rounded-lg p-3 text-left space-y-2">
                <Label htmlFor="admin-mfa-test-code" className="text-xs font-semibold text-emerald-900 block">
                  Verify 6-digit Authenticator Code:
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="admin-mfa-test-code"
                    type="text"
                    maxLength={6}
                    value={mfaVerifyCode}
                    onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="font-mono text-center text-base tracking-widest bg-white flex-1"
                  />
                  <Button
                    onClick={async () => {
                      if (!mfaVerifyCode || mfaVerifyCode.length !== 6) {
                        toast.error("Please enter a 6-digit code from Google Authenticator");
                        return;
                      }
                      setMfaLoading(true);
                      try {
                        const { data } = await api.post(`/admins/${editAdmin.id}/mfa-verify`, { code: mfaVerifyCode });
                        toast.success(data.message);
                        setAdminFormData({ ...adminFormData, mfa_enabled: true });
                        setAdminMfaSetupModal(false);
                        setMfaVerifyCode("");
                        loadAdmins();
                      } catch (err) {
                        toast.error(formatErr(err));
                      } finally {
                        setMfaLoading(false);
                      }
                    }}
                    disabled={mfaLoading || mfaVerifyCode.length !== 6}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4"
                  >
                    {mfaLoading ? "Verifying..." : "Verify & Activate MFA"}
                  </Button>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-md p-2 text-left">
                💡 <b>Instructions:</b> Scan QR code in Google Authenticator app, enter the 6-digit passcode above, and click <b>Verify & Activate MFA</b>.
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setAdminMfaSetupModal(false); setMfaVerifyCode(""); }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Admin Confirmation Modal */}
      <AlertDialog open={deleteConfirmModal} onOpenChange={setDeleteConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-600 flex items-center gap-2">
              <Trash2 className="size-5" /> Delete Admin Account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete admin account <strong className="text-slate-900">"{deleteTargetAdmin?.name}"</strong> ({deleteTargetAdmin?.username})?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTargetAdmin(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAdminAction} className="bg-rose-600 hover:bg-rose-700 text-white">
              Delete Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
